/**
 * visualTuning — single source of truth for every tunable VISUAL value
 * (reveal-sequence beat timings, screen-shake, parallax depths, particle budgets).
 *
 * Clones the audioTuning contract exactly (getVisualTuning / applyVisualTuning /
 * subscribeVisualTuning): persisted defaults live in visualTuning.json (written
 * back by the /studio visual tab). At runtime the studio mutates a live snapshot;
 * SceneStack / the sequencer / reveal screens subscribe and re-derive so slider
 * changes are visible instantly without a reload.
 */
import defaults from './visualTuning.json';

/**
 * A choreography sequence: an ordered map of BEAT NAME → offset in ms from the
 * sequence start. Keys ending in `Ms` are DURATION PARAMS consumed by cue
 * handlers (e.g. `tallyCountMs`, `moonRiseMs`), NOT beats that fire a timer — the
 * sequencer splits these apart (see sequencer.ts). Open-typed (Record<string,…>)
 * so new beats can be tuned in JSON without a type change.
 */
export type Sequence = Record<string, number>;

export interface VisualTuning {
  /** Named reveal sequences (LYNCH_REVEAL, DAWN_REVEAL, WIN_REVEAL, …). */
  sequences: Record<string, Sequence>;
  shake: {
    /** Base shake amplitude 0..1 (a reveal may scale this). */
    defaultIntensity: number;
    /** Shake settle duration in ms. */
    durationMs: number;
  };
  parallax: {
    /** Per-slot parallax factors (0 = static, higher = more travel). */
    sky: number;
    moon: number;
    far: number;
    mid: number;
    fore: number;
    /** Ambient drift loop period in seconds. */
    driftLoopS: number;
  };
  budgets: {
    /** Max live particles on the Stage view. */
    stageMaxParticles: number;
    /** Max live particles on a Hand (phone) view. */
    handMaxParticles: number;
    /** Film-grain overlay opacity 0..1. */
    grainOpacity: number;
  };
}

export type VisualTuningPatch = {
  // A string-index record is already "partial" (any subset of keys is valid), so
  // it is NOT wrapped in Partial<> — that would make its values `Sequence |
  // undefined` and poison the merged snapshot. Fixed-key sections use Partial<>.
  sequences?: Record<string, Sequence>;
  shake?: Partial<VisualTuning['shake']>;
  parallax?: Partial<VisualTuning['parallax']>;
  budgets?: Partial<VisualTuning['budgets']>;
};

function clone(t: VisualTuning): VisualTuning {
  return JSON.parse(JSON.stringify(t)) as VisualTuning;
}

let current: VisualTuning = clone(defaults as VisualTuning);
const listeners = new Set<(t: VisualTuning) => void>();

/** Live tuning snapshot. Read at USE time (not module load) so studio edits apply. */
export function getVisualTuning(): VisualTuning {
  return current;
}

/** Shallow-per-section merge + notify subscribers. */
export function applyVisualTuning(patch: VisualTuningPatch): void {
  current = {
    sequences: { ...current.sequences, ...patch.sequences },
    shake: { ...current.shake, ...patch.shake },
    parallax: { ...current.parallax, ...patch.parallax },
    budgets: { ...current.budgets, ...patch.budgets },
  };
  notify();
}

/** Replace the whole snapshot (studio Revert / load-from-server). */
export function setVisualTuning(next: VisualTuning): void {
  current = clone(next);
  notify();
}

/** Back to the values shipped in visualTuning.json. */
export function resetVisualTuning(): void {
  current = clone(defaults as VisualTuning);
  notify();
}

export function subscribeVisualTuning(cb: (t: VisualTuning) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function notify(): void {
  for (const cb of listeners) {
    try {
      cb(current);
    } catch {
      /* a listener throwing must not break tuning updates */
    }
  }
}
